/**
 * Service for managing the OAuth library's localStorage state.
 *
 * The @sangoma/react-oauth2-code-pkce library stores auth state in localStorage
 * with the 'ROCP_' prefix (React OAuth2 Code PKCE). It does NOT expose a public
 * API for pre-mount cleanup, so this service encapsulates direct localStorage access.
 *
 * See: node_modules/@sangoma/react-oauth2-code-pkce/dist/AuthContext.js
 * See: node_modules/@sangoma/react-oauth2-code-pkce/dist/authConfig.js (storageKeyPrefix default)
 */

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const SECONDS_TO_MS = 1000;

export class AuthStorageService {
  static readonly ROCP_KEY_PREFIX = 'ROCP_';

  readonly #keys = {
    loginInProgress: `${AuthStorageService.ROCP_KEY_PREFIX}loginInProgress`,
    token: `${AuthStorageService.ROCP_KEY_PREFIX}token`,
    tokenExpire: `${AuthStorageService.ROCP_KEY_PREFIX}tokenExpire`,
    refreshToken: `${AuthStorageService.ROCP_KEY_PREFIX}refreshToken`,
    refreshTokenExpire: `${AuthStorageService.ROCP_KEY_PREFIX}refreshTokenExpire`,
    hasLoggedIn: 'sangoma-meet-has-logged-in',
  } as const;

  /**
   * Clear a stale loginInProgress flag left by an interrupted OAuth redirect.
   *
   * Must be called BEFORE AuthProvider mounts (before root.render()),
   * because the library reads loginInProgress from localStorage in its useState
   * initializer and immediately throws "Bad authorization state" if it's true
   * without a ?code= URL parameter.
   */
  clearStaleLoginState(): void {
    const hasAuthCode = new URLSearchParams(window.location.search).has('code');
    if (hasAuthCode) {
      return;
    }

    try {
      if (localStorage.getItem(this.#keys.loginInProgress) === 'true') {
        console.warn('[Auth] Clearing stale loginInProgress flag from interrupted login');
        localStorage.setItem(this.#keys.loginInProgress, 'false');
      }
    } catch {
      // Ignore storage access errors (e.g. in private browsing)
    }
  }

  /**
   * Read the OAuth access token from localStorage and strip surrounding quotes.
   *
   * The ROCP library stores the token as a JSON-stringified value, which may
   * include wrapping double quotes. This method strips them for direct use
   * in Authorization headers.
   */
  getToken(): string {
    try {
      const rawToken = localStorage.getItem(this.#keys.token) || '';
      return rawToken.replace(/^"(.*)"$/, '$1');
    } catch {
      return '';
    }
  }

  /**
   * Log access token and refresh token expiration times to the console.
   * Intended to be called from the postLogin callback to aid debugging.
   */
  logTokenExpiry(): void {
    const tokenExpireDate = this.#parseExpireDate(this.#keys.tokenExpire);
    if (tokenExpireDate) {
      const hoursUntil = Math.round((tokenExpireDate.getTime() - Date.now()) / MS_PER_HOUR);
      console.log(
        `[Auth] Access token expires in ${hoursUntil}h at ${tokenExpireDate.toISOString()}`,
      );
    }

    const refreshExpireDate = this.#parseExpireDate(this.#keys.refreshTokenExpire);
    if (refreshExpireDate) {
      const daysUntil = Math.round((refreshExpireDate.getTime() - Date.now()) / MS_PER_DAY);
      console.log(
        `[Auth] Refresh token expires in ${daysUntil}d at ${refreshExpireDate.toISOString()}`,
      );
    }
  }

  markAsLoggedIn(): void {
    localStorage.setItem(this.#keys.hasLoggedIn, '1');
  }

  clearHasLoggedIn(): void {
    localStorage.removeItem(this.#keys.hasLoggedIn);
  }

  hasLoggedInBefore(): boolean {
    return localStorage.getItem(this.#keys.hasLoggedIn) === '1';
  }

  /**
   * Remove the ROCP refresh token from localStorage AND notify the library's
   * React state via a synthetic StorageEvent.
   *
   * The ROCP library uses a custom useBrowserStorage hook that only syncs
   * React state from localStorage through the "storage" event (which normally
   * only fires in other tabs). Dispatching the event manually makes the hook
   * set refreshToken to undefined, so the library's periodic interval takes
   * the "no refresh token" path and skips the network request entirely.
   */
  clearRefreshToken(): void {
    try {
      const key = this.#keys.refreshToken;
      localStorage.removeItem(key);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue: null,
          storageArea: localStorage,
        }),
      );
    } catch {
      // Ignore storage access errors
    }
  }

  #parseExpireDate(key: string): Date | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return null;
      }

      const seconds = parseInt(raw, 10);
      if (Number.isNaN(seconds)) {
        return null;
      }

      return new Date(seconds * SECONDS_TO_MS);
    } catch {
      return null;
    }
  }
}

export const authStorageService = new AuthStorageService();
